export type FAQEntry = {
  question: string;
  answer: string;
};

export type FAQCategory = {
  id: string;
  label: string;
  short: string;
  color: string;
  accent: string;
  activeAccent: string;
  dot: string;
  faqs: FAQEntry[];
};

export const faqCategories: FAQCategory[] = [
  {
    id: "abq",
    label: "Abiturbestenquote",
    short: "ABQ",
    color: "blue",
    accent: "bg-blue-100 text-blue-700 border-blue-200",
    activeAccent: "bg-blue-700 text-white border-blue-700",
    dot: "bg-blue-500",
    faqs: [
      {
        question: "Allgemeines zur Abiturbestenquote",
        answer:
          "Über die Abiturbestenquote (ABQ) werden 30 % der Studienplätze vergeben. In dieser Quote zählt nur das Abitur als Auswahlkriterium.\n\nZuerst wird für jedes Bundesland eine Landesliste gebildet. Hier konkurrieren die Bewerber der Studiengänge Humanmedizin, Zahnmedizin, Tiermedizin und Pharmazie miteinander, die ihr Abitur im selben Bundesland erworben haben.\n\nDas erste Sortierkriterium ist hierbei das Abiturergebnis, also die erreichte Abiturpunktzahl skaliert auf die maximal erreichbare Punktzahl von 900 Punkten. Sollte Punktgleichheit bestehen, zählt als nachrangiges Sortierkriterium ein abgeleisteter Dienst von 6 Monaten Dauer und zuletzt das Los.",
      },
      {
        question: "Reicht ein Abitur von 1,0 für die Abiturbestenquote?",
        answer:
          "Das hängt vom Bewerbungssemester und deinem Bundesland ab. In der ABQ zählt nicht allein der Durchschnitt, sondern die erreichte Gesamtpunktzahl.\n\nZum Beispiel müsstest du mit einem 1,0er Abitur in Berlin zum Sommersemester 2020 mindestens 823 Punkte aufweisen, um an der Charité angenommen zu werden. In NRW wären es dagegen nur 812 Punkte gewesen.",
      },
    ],
  },
  {
    id: "adh",
    label: "Auswahlverfahren der Hochschulen",
    short: "AdH",
    color: "cyan",
    accent: "bg-cyan-100 text-cyan-700 border-cyan-200",
    activeAccent: "bg-cyan-700 text-white border-cyan-700",
    dot: "bg-cyan-500",
    faqs: [
      {
        question: "Allgemeines zum Auswahlverfahren der Hochschulen (AdH)",
        answer:
          "Über das Auswahlverfahren der Hochschulen (AdH) werden 60 % der Studienplätze vergeben. Im AdH wählen die Universitäten selbst aus, welche Kriterien sie heranziehen und wie stark sie diese gewichten.\n\nAls Kriterien können neben Abiturnoten unter anderem Studierfähigkeitstests (TMS, HamNat, PhaST), Ausbildungen, Dienste, Wettbewerbe und Auswahlgespräche zählen.",
      },
      {
        question: "Was wird berücksichtigt?",
        answer:
          "Die Universitäten können sich aussuchen, welche Kriterien sie berücksichtigen und wie stark sie diese bepunkten.\n\nWelche Kriterien die verschiedenen Universitäten heranziehen und wie sie diese gewichten, kannst du in der spezifischen Auswertung der ncrechner App nachschauen.",
      },
      {
        question: "Was bedeuten die Unterquoten an manchen Universitäten?",
        answer:
          "Universitäten können das AdH in mehrere Unterquoten aufteilen. In jeder Unterquote können andere Kriterien und unterschiedliche Gewichtungen gelten.\n\nAm Beispiel RWTH Aachen: Die RWTH Aachen splittet das AdH in 3 Unterquoten: • 1. Unterquote: max. 95 Punkte für das Abitur, max. 5 Punkte für den TMS • 2. Unterquote: max. 20 Punkte für das Abitur, max. 70 Punkte für den TMS, max. 10 Punkte für einen Dienst",
      },
      {
        question: "Wie viele Punkte erhalte ich für einen Dienst?",
        answer:
          "Das ist universitätsabhängig. Beachte aber bitte, dass im AdH und in der ZEQ nur Dienste anerkannt werden, die im fachlich einschlägigen Bereich und über die volle vorhergesehene Dauer absolviert wurden.",
      },
    ],
  },
  {
    id: "zeq",
    label: "Zusätzliche Eignungsquote",
    short: "ZEQ",
    color: "indigo",
    accent: "bg-indigo-100 text-indigo-700 border-indigo-200",
    activeAccent: "bg-indigo-700 text-white border-indigo-700",
    dot: "bg-indigo-500",
    faqs: [
      {
        question: "Allgemeines zur zusätzlichen Eignungsquote",
        answer:
          "Über die zusätzliche Eignungsquote (ZEQ) werden 10 % der Studienplätze vergeben. In dieser Quote ist die Abiturnote irrelevant.\n\nAls notenunabhängige Kriterien gelten in der ZEQ zur Zeit die Studierfähigkeitstests, Ausbildungen, Dienste, Wettbewerbe, Auswahlgespräche und bis Ende 2022 noch die Wartesemester. Die Wartesemester galten im Jahr 2022 allerdings nur noch an den bayrischen Universitäten.",
      },
      {
        question: "Wie viele Punkte erhalte ich für die Wartezeit / Wartesemester?",
        answer:
          "Die Wartezeit wurde im Jahr 2022 nur noch an den bayrischen Universitäten berücksichtigt. Ab dem Sommersemester 2023 wird die Wartezeit gar nicht mehr berücksichtigt.",
      },
    ],
  },
  {
    id: "app",
    label: "Berechnungen in der App",
    short: "App",
    color: "violet",
    accent: "bg-violet-100 text-violet-700 border-violet-200",
    activeAccent: "bg-violet-700 text-white border-violet-700",
    dot: "bg-violet-500",
    faqs: [
      {
        question: "Was sagen die Berechnungen in der App aus?",
        answer:
          "Die App zeigt euch an, ob ihr in den vergangenen Semestern einen Studienplatz erhalten hättet. Das ist eine sehr gute Orientierung, da die Grenzen für gewöhnlich nicht stark schwanken.\n\nWenn ihr die letzten Semester immer an einer Universität angenommen worden wäret, dann ist die Wahrscheinlichkeit auch hoch, dass es in der Zukunft für eine Zulassung ausreicht.",
      },
      {
        question: "Sind die Berechnungen korrekt?",
        answer:
          "Ja, wenn die Angaben korrekt eingetragen werden. Bitte tragt bei den Angaben für das Abitur euren Prozentrang ein. Diesen bekommt ihr jedes Semester von Hochschulstart mitgeteilt und findet ihn in eurem Bewerberkonto unter den festen Bewerbungsbestandteilen.\n\nNur über den Prozentrang kann die Punktzahl für das Abitur im AdH korrekt berechnet werden!",
      },
      {
        question: "Werde ich auf jeden Fall nächstes Semester angenommen?",
        answer:
          "Eine Garantie kann die App nicht geben. Die App kann nur eine sehr gute Orientierung schaffen, da die Grenzen für gewöhnlich nicht stark schwanken.",
      },
      {
        question: "Ich bin Bewerber:in aus dem Ausland. Kann ich die ncrechner App trotzdem verwenden?",
        answer:
          "Ja. Bewerber aus dem Ausland erhalten per Zufall ein Bundesland zugeteilt. Wenn ihr noch keinen Prozentrang zugeteilt bekommen habt, empfehlen wir euch das Bundesland Brandenburg auszuwählen und euch den entsprechenden Prozentrang zu eurem Abiturergebnis aus den hinterlegten Tabellen rauszusuchen.\n\nDas Bundesland Brandenburg ist das derzeit „schlechteste“ Bundesland im Hinblick auf die Prozentrangverteilung, sodass dies dann euren „worst-case“ darstellt.",
      },
    ],
  },
  {
    id: "dienste",
    label: "Dienste",
    short: "FSJ / BFD",
    color: "emerald",
    accent: "bg-emerald-100 text-emerald-700 border-emerald-200",
    activeAccent: "bg-emerald-700 text-white border-emerald-700",
    dot: "bg-emerald-500",
    faqs: [
      {
        question: "Mein Dienst wurde nicht anerkannt",
        answer:
          "Ein Dienst von 6 Monaten Dauer zählt als nachrangiges Auswahlkriterium in der Abiturbestenquote. Hierbei ist der Bereich, in dem der Dienst absolviert wurde, irrelevant.\n\nMöchtest du deinen Dienst allerdings als Kriterium im AdH und in der ZEQ anrechnen lassen, muss der Dienst über volle 11 Monate (bis zum Bewerbungsstichtag) absolviert worden sein und zudem im fachlich einschlägigen Bereich stattgefunden haben.",
      },
      {
        question: "Wo soll ich meinen Dienst absolvieren?",
        answer:
          "Wir empfehlen, den Dienst im Krankenhaus oder im Rettungsdienst zu absolvieren, da hier die fachliche Einschlägigkeit gegeben ist. Zudem habt ihr den Vorteil der Anerkennung des Pflegepraktikums.",
      },
    ],
  },
  {
    id: "ausbildung",
    label: "Ausbildungen",
    short: "Ausbildung",
    color: "rose",
    accent: "bg-rose-100 text-rose-700 border-rose-200",
    activeAccent: "bg-rose-700 text-white border-rose-700",
    dot: "bg-rose-500",
    faqs: [
      {
        question: "Meine Ausbildung wurde nicht anerkannt",
        answer:
          "Es werden nur die Ausbildungen anerkannt, die ihr in unserer Auflistung bzw. in den Auswahlkatalogen auf Hochschulstart.de finden könnt.\n\nSeit dem neuen Verfahren werden einige Berufsausbildungen leider nicht mehr berücksichtigt. Hierzu zählt beispielsweise die Ausbildung zum/zur CTA (chirurgisch technischen Assistent/-in).\n\nHabt ihr das Problem, dass eure Ausbildung nicht anerkannt wird, könnt ihr uns gerne per Mail kontaktieren und wir können euch an kompetente Anwälte weiterleiten.",
      },
      {
        question: "Ab wann zählt die Berufstätigkeit?",
        answer:
          "Die Berufstätigkeit zählt ab einer Mindestdauer von 12 Monaten, wenn vorher die entsprechende Ausbildung erfolgreich abgeschlossen wurde. Die Ausbildungszeit zählt somit nicht als Berufstätigkeit / Berufserfahrung.",
      },
    ],
  },
];
